/**
 * Les libellés que le moteur ne donne plus.
 *
 * `shared` rendait des phrases françaises — « 611 — paire d'as », « La cave », « Gagner
 * en séchant ». Une phrase n'est pas une donnée : elle ne se traduit pas depuis un
 * moteur de règles, et elle ne se teste qu'en la comparant à elle-même. Le moteur rend
 * donc ce qu'il SAIT (une famille, une étape, un type de défi) et c'est ici qu'on
 * l'habille, dans la langue de l'écran.
 *
 * ⚠️ Le **vocabulaire reste tel quel** dans les deux langues : 421, nénette, cave,
 * recave, sec, rampeau. C'est le nom des choses. Ce qu'on traduit, c'est ce qu'il y a
 * autour — « paire d'as » devient « pair of aces », « 611 » ne devient rien.
 */

import type { Combinaison, Defi, Etape } from "@zinc421/shared";

import type { Textes } from "../i18n/index.js";

/** « 611 — paire d'as ». La clé ne se traduit pas, la description si. */
export function nomDeCombinaison(combinaison: Combinaison, textes: Textes): string {
  switch (combinaison.famille) {
    case "421": return textes.comboQuatreCentVingtEtUn;
    case "brelanAs": return `${combinaison.cle} — ${textes.comboBrelanAs}`;
    case "brelan": return `${combinaison.cle} — ${textes.comboBrelan(combinaison.hauteur)}`;
    case "paireAs": return `${combinaison.cle} — ${textes.comboPaireAs}`;
    case "nenette": return `${combinaison.cle} — ${textes.comboNenette}`;
    case "suite": return `${combinaison.cle} — ${textes.comboSuite}`;
    // Une « lecture » se lit telle quelle : il n'y a rien à en dire de plus.
    default: return combinaison.cle;
  }
}

/**
 * « La cave », « La recave », « La cave des caves ».
 *
 * Identiques dans les deux langues, et c'est voulu — ce sont les noms des trois
 * manches d'une soirée, pas une description. Seul « Terminé » se traduit.
 */
export function nomDeLEtape(etape: Etape, textes: Textes): string {
  switch (etape) {
    case "CAVE": return textes.etapeCave;
    case "RECAVE": return textes.etapeRecave;
    case "FINALE": return textes.etapeFinale;
    default: return textes.etapeFini;
  }
}

/**
 * Le code d'étape envoyé par le serveur, habillé.
 *
 * ⚠️ Le serveur envoie « CAVE », pas « La cave » : il ne connaît pas la langue de ses
 * joueurs, et deux clients d'une même table peuvent ne pas la partager. Une valeur
 * inconnue rend une chaîne vide plutôt qu'un code brut — mieux vaut ne rien afficher
 * qu'afficher « RECAVE » au milieu d'une phrase.
 */
export function nomDeLEtapeReseau(code: string, textes: Textes): string {
  if (code === "CAVE" || code === "RECAVE" || code === "FINALE" || code === "FINI") {
    return nomDeLEtape(code, textes);
  }
  return "";
}

export function libelleDefi(defi: Defi, textes: Textes): string {
  switch (defi.type) {
    case "AVEC_421": return textes.defiAvec421;
    case "AVEC_SEC": return textes.defiAvecSec;
    case "SANS_RAMPEAU": return textes.defiSansRampeau;
    case "EN_MOINS_DE": return textes.defiEnMoinsDe(defi.manches);
    case "AU_PLUS": return textes.defiAuPlus(defi.jetons);
  }
}
